import React from 'react';
import {PulseLoader} from 'react-spinners';
import {Box, Typography} from '@mui/material';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import EmptyContent from '@components/empty-content/empty-content';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import {DataGridToolTipTypography} from '@modules/home/main/sub-modules-menu/collapsible-table/CollapsibleTableStyle';

const isOverflown = (element: Element): boolean => {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
};

export const NoRowsOverlay = (props: any) => {
  return props?.loading ? (
    <CircularProgressDiv height="150px">
      <PulseLoader size={10} color={props.theme.palette.primary.main} />
    </CircularProgressDiv>
  ) : (
    <EmptyContent
      sx={{
        py: 10
      }}
      title={props?.noRowsFound ? 'No Results Found' : 'No Data'}
    />
  );
};

export const GridCellRender = React.memo(function GridCellExpand(props: any) {
  const {width, value} = props;
  const wrapper = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  const renderValueTypography = () => {
    return (
      <Typography
        ref={cellValue}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...props?.typographySx
        }}>
        {value}
      </Typography>
    );
  };

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex'
      }}>
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0
        }}
      />
      {renderValueTypography()}
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{width, marginLeft: -17}}>
          <Paper
            elevation={1}
            style={{
              minHeight: wrapper.current!.offsetHeight - 3,
              maxWidth: '40vw',
              overflowY: 'auto'
            }}
            sx={{backgroundColor: 'rgba(0, 47, 85, 0.81)'}}>
            <DataGridToolTipTypography variant="subtitle1">
              {value}
            </DataGridToolTipTypography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});
